import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { TeamsTeamListPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import companies from 'modules/core/store/collections/companies'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import gendersCollection from 'modules/settings/store/collections/genders'
import legalGendersCollection from 'modules/settings/store/collections/legalGenders'
import TeamsList from 'modules/teams/components/List'
import TeamChartActivationBanner from 'modules/teams/components/TeamChartActivationBanner'
import { TeamsShiftLocationProvider } from 'modules/teams/components/teamsShiftLocationContext'
import teamsCollection from 'modules/teams/store/collections/teams'
import teamsAnalytics from 'modules/teams/store/collections/teamsAnalytics'
import teamsShiftLocation from 'modules/teams/store/collections/teamsShiftLocation'
import { WORKING_LOCATION_RANGE_DAYS } from 'modules/teams/store/models/TeamShiftLocationAnalytics'

const fetchLocationAnalytics = (hasClockInLocationEnabled: boolean, teamIds: number[]) => {
  const days = WORKING_LOCATION_RANGE_DAYS
  const thirtyDaysAgo = moment().subtract(days, 'day')
  const hasLocationEnabled = hasClockInLocationEnabled

  if (!hasLocationEnabled || teamIds.length === 0) {
    return Promise.resolve()
  }

  return teamsShiftLocation.fetch({
    data: { from_date: thirtyDaysAgo.toDate(), ids: teamIds, group_by: 'team' },
  })
}

const TeamsListPage = () => {
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(TeamsTeamListPageDocument)
  const teams = data?.teams.teams
  const canEmployeeSeeAggregatedPersonalInfo =
    data?.employees._policies.employeeSeeAggregatedPersonalInfo.can ?? false

  const teamsIds = teams?.map((team) => team.id) || []

  const canCreateTeams = Boolean(data?.teams._policies.teamCreate.can)

  const { loading: loadingAnalytics } = useAsyncData(() => {
    if (!teamsIds.length) {
      return Promise.resolve()
    }

    return Promise.all([
      teamsCollection.fetch(),
      teamsAnalytics.fetch(),
      gendersCollection.fetch(),
      legalGendersCollection.fetch(),
      fetchLocationAnalytics(companies.current.attributes.has_clock_in_location_enabled, teamsIds),
    ])
  }, [`${teamsIds}`])

  return (
    <TeamsShiftLocationProvider collection={teamsShiftLocation}>
      <ResponsivePage.Body loading={loading} error={null} showHeader={false}>
        <Box gap="s24">
          {canCreateTeams && <TeamChartActivationBanner companyId={company.id} />}
          <TeamsList
            loadingAnalytics={loadingAnalytics}
            teams={teams || []}
            canEmployeeSeeAggregatedPersonalInfo={canEmployeeSeeAggregatedPersonalInfo}
          />
        </Box>
        <Outlet />
      </ResponsivePage.Body>
    </TeamsShiftLocationProvider>
  )
}

export default observer(TeamsListPage)
